import { Box, Grid2, Typography, Link } from '@mui/material'
import { PaymentsParams, ReviewPaymentData } from 'api/payments/types'
import { useAuth } from 'components/AuthProvider'
import Button from 'components/Button/Button'
import DepositDetail from 'components/DepositDetail'
import { usePayments } from 'components/PaymentsProvider'
import LoadingWrapper from 'components/layout/LoadingWrapper/LoadingWrapper'
import {
  useMutatePoliDirectDebit,
  useMutateBlinkpayDirectDebit
} from 'hooks/useTransfers'
import { useClientConfig } from 'hooks/useClientConfig'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useNavigate, useLocation } from 'react-router-dom'
import { rechargePath } from 'components/Routes/Routes'
import { roundUpDollarsAndCents } from 'utils/util'
import CreditOrDebitCard from './CreditOrDebitCard'
import useStyles from './styles'

const DEFAULT_WALLET_USE_AMOUNT = 1

interface TransactionButtonsProps {
  price: number
  setDisabledButton: (disabled: boolean) => void
  handlePaymentSuccess?: (verify?: { orderExecuted: boolean }) => void
  paymentParams?: PaymentsParams
  handleCreatePaymentSuccess?: (payment: ReviewPaymentData) => void
  loading?: boolean
  linkToRecharge?: boolean
  hideBankTransfer?: boolean
  /**
   * Pay-for-order: the review order this top-up funds. When set, BOTH payment
   * methods are LINKED to that order and settlement executes the purchase at
   * the quoted price server-side.
   *
   * Without it the payment is a bare deposit against a quote with a 5-minute
   * TTL. BlinkPay's bank redirect routinely outlives it (ticket #2415) — and
   * so does typing card details: the card path's "complete the trade in the
   * success callback" assumption failed exactly there, charging the card and
   * then refusing the buy with "Review order has expired" [BUG-016]. A linked
   * card payment executes during verify instead (verify.orderExecuted tells
   * the callback the trade is already done).
   */
  reviewOrderId?: number
}

enum EActivatedButton {
  CreditOrDebit = 'CreditOrDebit',
  POLiTransfer = 'POLiTransfer',
  BlinkPayPayment = 'BlinkPayPayment',
  BankTransfer = 'BankTransfer'
}

const TransactionButtons = ({
  price,
  setDisabledButton,
  handlePaymentSuccess,
  paymentParams,
  handleCreatePaymentSuccess,
  loading,
  linkToRecharge,
  hideBankTransfer,
  reviewOrderId
}: TransactionButtonsProps): JSX.Element => {
  const navigate = useNavigate()
  const { classes } = useStyles()

  const { user, setToast } = useAuth()

  const location = useLocation()

  const [activatedButton, setActivatedButton] =
    useState<EActivatedButton | null>(null)

  const { createPayment, loading: paymentLoading, error } = usePayments()

  useEffect(() => {
    if (error) {
      setToast({
        open: true,
        duration: 3000,
        type: 'error',
        message: error
      })
    }
  }, [error, setToast])

  const {
    mutate: createPoliDirectDebit,
    isLoading: createPoliDirectDebitLoading
  } = useMutatePoliDirectDebit()

  const {
    mutate: createBlinkpayDirectDebit,
    isLoading: createBlinkpayDirectDebitLoading
  } = useMutateBlinkpayDirectDebit()

  // POLi retires 30 Jun (replaced by BlinkPay). Gated by the server-side
  // `poli_enabled` feature flag; hidden unless explicitly enabled.
  const { data: clientConfig } = useClientConfig()
  const poliEnabled = clientConfig?.features?.poli_enabled ?? false

  const firstTimeTopUpWithPoli = useMemo(() => {
    return Boolean(user?.firstTimeTopUpWithPoli)
  }, [user])

  const firstTimeTopUpWithBlinkpay = useMemo(() => {
    return Boolean(user?.firstTimeTopUpWithBlinkpay)
  }, [user])

  const loadingButton = useMemo(() => {
    return (
      paymentLoading ||
      createPoliDirectDebitLoading ||
      createBlinkpayDirectDebitLoading ||
      Boolean(loading)
    )
  }, [
    createBlinkpayDirectDebitLoading,
    createPoliDirectDebitLoading,
    loading,
    paymentLoading
  ])

  const creditFee = useMemo(() => {
    const balanceInWallet =
      price < 1 ? price + DEFAULT_WALLET_USE_AMOUNT : price

    const formulaFee: number =
      (balanceInWallet +
        Number(process.env.REACT_APP_STRIPE_PROCESSING_FEE_CHARGE)) /
      (1 - Number(process.env.REACT_APP_STRIPE_PROCESSING_FEE_PERCENTAGE) / 100)

    return Number(formulaFee - balanceInWallet).toFixed(2)
  }, [price])

  const poliFee = useMemo(() => {
    // Include a 1% fee, capped at 4$.
    return firstTimeTopUpWithPoli ? Number(Math.min(0.01 * price, 3)) : 0
  }, [firstTimeTopUpWithPoli, price])

  const blinkpayFeePercentage =
    Number(process.env.REACT_APP_BLINKPAY_FEE_PERCENTAGE) || 1
  const blinkpayFeeMax = Number(process.env.REACT_APP_BLINKPAY_FEE_MAX) || 3
  const blinkpayFee = useMemo(() => {
    // 1% capped at $3 — and rounded UP to whole cents, which is load-bearing.
    //
    // We send `price + fee` as the deposit; the API carves the fee back out of that
    // gross and credits the rest, rounding the fee to cents (round_half_up, Rails
    // parity). An unrounded fee here means it carves out MORE than we added, so the
    // wallet is credited less than the top-up the checkout screen asked for — and the
    // buyer-balance check compares against an unquantized order total, so a fraction
    // of a cent is enough to refuse the trade.
    //
    // Measured on staging before this: top-up $59.78, fee added $0.5978, fee carved
    // $0.60, wallet credited $59.7778 against $59.779366 required. Short by $0.0016,
    // and the customer is returned to checkout still owing — the same dead end as the
    // $0.00-fee bug, three decimal places down.
    //
    // Rounding up cannot overshoot into a second failure: the extra is at most a cent
    // and it lands in the customer's own wallet.
    if (!firstTimeTopUpWithBlinkpay) return 0
    return roundUpDollarsAndCents(
      Math.min((price * blinkpayFeePercentage) / 100, blinkpayFeeMax)
    )
  }, [blinkpayFeeMax, blinkpayFeePercentage, firstTimeTopUpWithBlinkpay, price])

  const allowedBlinkpayEmails =
    process.env.REACT_APP_BLINKPAY_ALLOWED_USER_EMAILS || ''
  const isBlinkpayAllowedForUser = useMemo(() => {
    if (!allowedBlinkpayEmails) return true
    const allowedList = allowedBlinkpayEmails
      .split(',')
      .map(e => e.trim().toLowerCase())
    return allowedList.includes(user?.email?.toLowerCase() || '')
  }, [allowedBlinkpayEmails, user?.email])

  const recaptchaEnabled = JSON.parse(
    process.env.REACT_APP_RECAPTCHA_ENABLED || 'true'
  )

  const { executeRecaptcha } = useGoogleReCaptcha()

  const scrollToBottom = useCallback((timer?: number, selectorStr?: string) => {
    const selector = selectorStr ?? 'body'
    setTimeout(() => {
      const bodyElem = document.querySelector(selector)
      if (bodyElem) {
        bodyElem.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
          inline: 'end'
        })
      }
    }, timer ?? 500)
  }, [])

  const handleClickButton = (type: EActivatedButton) => {
    setActivatedButton(type)
    setDisabledButton(true)
  }

  const handleClickPOLiButton = async () => {
    setDisabledButton(true)
    setActivatedButton(EActivatedButton.POLiTransfer)
    if (paymentParams && paymentParams.payment_type === 'top_up_to_wallet') {
      localStorage.setItem(
        'url_after_POLi_transfer',
        location.pathname + location.search
      )
    } else if (
      paymentParams &&
      paymentParams.payment_type === 'top_up_to_wallet_for_voucher'
    ) {
      localStorage.setItem('voucher_form', JSON.stringify(paymentParams))
    }

    createPoliDirectDebit(
      { amount: price + poliFee, productId: 0 },
      {
        onSuccess: data => {
          if (data.navigationUrl) {
            window.location.replace(data.navigationUrl)
          }
        },
        onError: (error: unknown) => {
          const message =
            error instanceof Error ? error.message : 'An error occurred'
          setToast({
            open: true,
            duration: 3000,
            type: 'error',
            message
          })
        }
      }
    )
  }

  const handleClickBlinkPayButton = async () => {
    setDisabledButton(true)
    setActivatedButton(EActivatedButton.BlinkPayPayment)
    if (paymentParams && paymentParams.payment_type === 'top_up_to_wallet') {
      localStorage.setItem(
        'url_after_blinkpay_payment',
        location.pathname + location.search
      )
      // Remember that this deposit FUNDS an order. Settlement buys the gold
      // server-side, so on the way back there is nothing for the customer to do —
      // and returning them to the checkout screen, which still reads "YOU'LL NEED TO
      // PAY", invites them to pay a second time. See BlinkPayTransferStatus.
      if (reviewOrderId) {
        localStorage.setItem('blinkpay_review_order_id', String(reviewOrderId))
      }
    } else if (
      paymentParams &&
      paymentParams.payment_type === 'top_up_to_wallet_for_voucher'
    ) {
      localStorage.setItem('voucher_form', JSON.stringify(paymentParams))
    }

    createBlinkpayDirectDebit(
      { amount: price + blinkpayFee, productId: 0, reviewOrderId },
      {
        onSuccess: data => {
          if (data.navigationUrl) {
            window.location.replace(data.navigationUrl)
          }
        },
        onError: (error: unknown) => {
          const message =
            error instanceof Error ? error.message : 'An error occurred'
          setToast({
            open: true,
            duration: 3000,
            type: 'error',
            message
          })
        }
      }
    )
  }

  const handleCreatePayment = async () => {
    if (!executeRecaptcha && recaptchaEnabled) return

    try {
      const recaptchaToken =
        recaptchaEnabled && executeRecaptcha
          ? await executeRecaptcha('payments/create')
          : undefined

      createPayment(
        {
          amount: paymentParams?.amount?.toString() ?? price.toString(),
          payment_type: paymentParams?.payment_type ?? 'top_up_to_wallet',
          note: paymentParams?.note,
          toEmail: paymentParams?.toEmail,
          toName: paymentParams?.toName,
          stripe_token: paymentParams?.stripe_token,
          review_order_id: reviewOrderId
        },
        recaptchaToken,
        res => {
          setActivatedButton(EActivatedButton.CreditOrDebit)
          setDisabledButton(false)
          scrollToBottom(1000, '#buy-confirmation-button')
          if (handleCreatePaymentSuccess) {
            handleCreatePaymentSuccess(res.review_data)
          }
        }
      )
    } catch (e) {
      return
    }
  }

  const renderPaymentMethods = (activatedButton: EActivatedButton | null) => {
    switch (activatedButton) {
      case EActivatedButton.CreditOrDebit:
        scrollToBottom(2000, '#buy-confirmation-button')
        return (
          <CreditOrDebitCard
            handlePaymentSuccess={handlePaymentSuccess}
            setDisabledButton={setDisabledButton}
          />
        )

      case EActivatedButton.BankTransfer:
        scrollToBottom()
        return <DepositDetail />

      default:
        return <></>
    }
  }

  const getClassButton = (typeButton: EActivatedButton) => {
    return activatedButton === typeButton
      ? classes.wrapperActivatedBtn
      : classes.wrapperBtn
  }

  const getColorButton = (typeButton: EActivatedButton) => {
    return activatedButton === typeButton ? 'primary' : 'default'
  }

  return (
    <LoadingWrapper loading={loadingButton} centred>
      <Grid2 container mt={1} spacing={2} className={classes.root}>
        <Grid2 size={{ xs: 12 }}>
          <Button
            fullWidth
            color={getColorButton(EActivatedButton.CreditOrDebit)}
            type='button'
            onClick={handleCreatePayment}
            disabled={loadingButton}
            className={getClassButton(EActivatedButton.CreditOrDebit)}
          >
            <Box>
              <Typography className={classes.titleBtn}>
                Credit or Debit Card
              </Typography>
              <Typography className={classes.textFee}>
                plus PROCESSING FEE, +${creditFee}
              </Typography>
            </Box>
          </Button>
        </Grid2>
        {poliEnabled && (
          <Grid2 size={{ xs: 12 }}>
            <Button
              fullWidth
              color={getColorButton(EActivatedButton.POLiTransfer)}
              type='button'
              onClick={handleClickPOLiButton}
              disabled={loadingButton}
              className={getClassButton(EActivatedButton.POLiTransfer)}
            >
              <Box>
                <Typography className={classes.titleBtn}>
                  POLi Transfer
                </Typography>
                <Typography className={classes.textFee}>
                  Plus PROCESSING FEE, +${poliFee.toFixed(2)}, INSTANT DEPOSIT
                </Typography>
              </Box>
            </Button>
          </Grid2>
        )}
        {isBlinkpayAllowedForUser && (
          <Grid2 size={{ xs: 12 }}>
            <Button
              fullWidth
              color={getColorButton(EActivatedButton.BlinkPayPayment)}
              type='button'
              onClick={handleClickBlinkPayButton}
              disabled={loadingButton}
              className={getClassButton(EActivatedButton.BlinkPayPayment)}
            >
              <Box>
                <Typography className={classes.titleBtn}>
                  BlinkPay Payment
                </Typography>
                <Typography className={classes.textFee}>
                  Plus PROCESSING FEE, +${blinkpayFee.toFixed(2)}, INSTANT
                  DEPOSIT
                </Typography>
              </Box>
            </Button>
          </Grid2>
        )}
        {!hideBankTransfer && (
          <Grid2 size={{ xs: 12 }}>
            {linkToRecharge ? (
              <Link
                onClick={() =>
                  navigate(rechargePath(), {
                    state: { amount: price }
                  })
                }
              >
                <Box sx={{}}>
                  <Typography
                    className={classes.titleBtn}
                    sx={{ textAlign: 'center' }}
                    color='primary'
                  >
                    Bank transfer... and return later
                  </Typography>
                  <Typography
                    className={classes.textFee}
                    sx={{ textAlign: 'center' }}
                    color='primary'
                  >
                    NO PROCESSING FEE, DEPOSIT 2-4 BUSINESS DAYS
                  </Typography>
                </Box>
              </Link>
            ) : (
              <Button
                fullWidth
                color={getColorButton(EActivatedButton.BankTransfer)}
                type='button'
                onClick={() => handleClickButton(EActivatedButton.BankTransfer)}
                disabled={loadingButton}
                className={getClassButton(EActivatedButton.BankTransfer)}
              >
                <Box>
                  <Typography className={classes.titleBtn}>
                    Bank transfer
                  </Typography>
                  <Typography className={classes.textFee}>
                    NO PROCESSING FEE, DEPOSIT 2-4 BUSINESS DAYS
                  </Typography>
                </Box>
              </Button>
            )}
          </Grid2>
        )}

        <Grid2 size={{ xs: 12 }}>
          <Box>{renderPaymentMethods(activatedButton)}</Box>
        </Grid2>
      </Grid2>
    </LoadingWrapper>
  )
}

export default TransactionButtons
